import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { inventoryService } from '../../services/inventoryService';
import type {
  CompleteFinishedGoodsProductionDraftRequest,
  CreateFinishedGoodsProductionDraftNewRequest,
  CreateFinishedGoodsProductionDraftRequest,
  FinishedGoodsCostEstimateRequest,
  FinishedGoodsCostEstimateResponse,
  FinishedGoodsProductionDetail,
  FinishedGoodsProductionListItem,
  FinishedGoodsProductionRequest,
  FinishedGoodsProductionResponse,
} from '../../services/inventory/inventoryMappers';
import { useToast } from '../../components/common/Toast';
import { useSettingsStore } from '../../store/settingsStore';
import type { InventoryItem, RoastedBulkGroup } from '../../types/inventory';
import { useWeightUnit } from '../useWeightUnit';
import { useLanguage } from '../../features/language/useLanguage';
import { useOnboardingStore } from '../../store/onboardingStore';

export type FinalProductMode = 'existing' | 'new';
export type FinishedGoodsProductionModalMode = 'create_draft' | 'complete_draft' | 'view';

export interface FinishedGoodsProductionFormValues {
  finalProductMode: FinalProductMode;
  finalProductItemId?: string;
  finalProductName?: string;
  finalProductLocation?: string;
  estimatedSellingPrice?: number;
  finalQuantityPcs: number;
  totalInputCost?: number;
  unitCostPerPiece?: number;
  packSizeKg: number;
  components: {
    id: string;
    roastedBulkItemId?: string;
    roastType?: string;
    itemName?: string;
    availableKg: number;
    quantityKg: number;
    basePerPieceKg: number;
    gramsPerPack: number;
    finalGramsPerPack?: number;
    percentage: number;
  }[];
  miscCosts: {
    id: string;
    name: string;
    unitPrice: number;
    unit: number;
  }[];
}

const componentSchemaDraft = z.object({
  id: z.string(),
  roastedBulkItemId: z.string().optional(),
  roastType: z.string().optional(),
  itemName: z.string().optional(),
  availableKg: z.number().nonnegative(),
  quantityKg: z.number().nonnegative(),
  basePerPieceKg: z.number().nonnegative(),
  gramsPerPack: z.number().nonnegative(),
  finalGramsPerPack: z.number().nonnegative().optional(),
  percentage: z.number().min(0).max(100),
});

const componentSchemaComplete = z.object({
  id: z.string(),
  roastedBulkItemId: z.string().min(1, 'Roasted bulk item is required'),
  roastType: z.string().min(1, 'Roast type is required'),
  itemName: z.string().optional(),
  availableKg: z.number().nonnegative(),
  quantityKg: z.number().nonnegative(),
  basePerPieceKg: z.number().nonnegative(),
  gramsPerPack: z.number().nonnegative(),
  finalGramsPerPack: z.number().nonnegative().optional(),
  percentage: z.number().min(0).max(100),
});

const miscCostSchema = z.object({
  id: z.string(),
  name: z.string(),
  unitPrice: z.number().nonnegative(),
  unit: z.number().nonnegative(),
});

const buildFinishedGoodsSchema = (mode: FinishedGoodsProductionModalMode) =>
  z
    .object({
      finalProductMode: z.enum(['existing', 'new']),
      finalProductItemId: z.string().optional(),
      finalProductName: z.string().optional(),
      finalProductLocation: z.string().optional(),
      estimatedSellingPrice: z.number().nonnegative().optional(),
      finalQuantityPcs: z.number().positive(),
      packSizeKg: mode === 'create_draft' ? z.number().nonnegative() : z.number().positive(),
      components: z.array(mode === 'create_draft' ? componentSchemaDraft : componentSchemaComplete),
      miscCosts: z.array(miscCostSchema),
    })
    .superRefine((data, ctx) => {
      const hasFinalProduct =
        (data.finalProductMode === 'existing' && !!data.finalProductItemId) ||
        (data.finalProductMode === 'new' && !!data.finalProductName);
      if (!hasFinalProduct) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Final product name is required',
          path: ['finalProductName'],
        });
      }

      if (mode === 'create_draft') {
        if (data.finalProductMode === 'new' && (!data.packSizeKg || data.packSizeKg <= 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Pack size is required and must be a positive integer.',
            path: ['packSizeKg'],
          });
        }
        return;
      }

      if (!data.components || data.components.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one component is required',
          path: ['components'],
        });
      }

      const keys = (data.components || [])
        .filter(c => c?.roastedBulkItemId && c?.roastType)
        .map(c => `${c.roastedBulkItemId}:${c.roastType}`);
      if (new Set(keys).size !== keys.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Roasted bulk item tidak boleh dipilih dua kali',
          path: ['components'],
        });
      }
    });

const defaultComponentRow = (availableKg = 0): FinishedGoodsProductionFormValues['components'][number] => ({
  id: Math.random().toString(36).slice(2),
  roastedBulkItemId: '',
  roastType: '',
  availableKg,
  quantityKg: 0,
  basePerPieceKg: 0,
  gramsPerPack: 0,
  finalGramsPerPack: 0,
  percentage: 0,
});

const defaultMiscCostRow = (): FinishedGoodsProductionFormValues['miscCosts'][number] => ({
  id: Math.random().toString(36).slice(2),
  name: '',
  unitPrice: 0,
  unit: 0,
});

const EMPTY_COMPONENTS: FinishedGoodsProductionFormValues['components'] = [];
const EMPTY_MISC_COSTS: FinishedGoodsProductionFormValues['miscCosts'] = [];

const normalizeFinishedGoodName = (value: string | undefined | null): string =>
  (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

export type FinishedGoodsProductionResult =
  | { kind: 'draft_created'; productionId: string; finishedGoodId?: string; finalQuantityPcs: number }
  | { kind: 'completed'; request: FinishedGoodsProductionRequest; response: FinishedGoodsProductionResponse; productionId?: string };

interface UseFinishedGoodsProductionFormParams {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: FinishedGoodsProductionResult) => void;
  mode: FinishedGoodsProductionModalMode;
  productionId?: string;
}

export const useFinishedGoodsProductionForm = ({
  isOpen,
  onClose,
  onSuccess,
  mode,
  productionId,
}: UseFinishedGoodsProductionFormParams) => {
  const { t } = useLanguage();
  const { showToast } = useToast();
  const { defaultUnit } = useSettingsStore();
  const { unit, toggleUnit } = useWeightUnit(defaultUnit as 'kg' | 'gram');
  // Gate all onboarding-specific behaviour on step 5 being in_progress so that
  // normal (non-onboarding) usage is completely unaffected.
  const onboardingStep5 = useOnboardingStore(
    (s) => s.state?.status === 'in_progress' && s.state?.currentStep === 5,
  );
  // Restrict the packing pickers to the tagged sample roasted bulks / finished
  // good while the practice step runs. Roasted-bulk groups have no per-row sample
  // flag, so match them by the sample ids in sampleRefs.
  const sampleRefs = useOnboardingStore((s) => s.state?.sampleRefs);
  const [finishedGoodsItems, setFinishedGoodsItems] = useState<InventoryItem[]>([]);
  const [roastedBulkGroups, setRoastedBulkGroups] = useState<RoastedBulkGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftDetail, setDraftDetail] = useState<FinishedGoodsProductionDetail | null>(null);
  const [hasLoadedDraftData, setHasLoadedDraftData] = useState(false);
  const [finalQuantityInput, setFinalQuantityInput] = useState('');
  const [componentQuantityInputs, setComponentQuantityInputs] = useState<
    Record<string, { percentage?: string; grams?: string }>
  >({});
  const prevFinalQuantityRef = useRef<number>(0);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    register,
    formState,
  } = useForm<FinishedGoodsProductionFormValues, unknown, FinishedGoodsProductionFormValues>({
    resolver: zodResolver(buildFinishedGoodsSchema(mode)) as unknown as Resolver<
      FinishedGoodsProductionFormValues,
      unknown,
      FinishedGoodsProductionFormValues
    >,
    defaultValues: {
      finalProductMode: 'existing',
      finalProductItemId: undefined,
      finalProductName: '',
      finalProductLocation: '',
      estimatedSellingPrice: undefined,
      finalQuantityPcs: 0,
      packSizeKg: 0,
      components: [defaultComponentRow()],
      miscCosts: [defaultMiscCostRow()],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'components',
    keyName: 'rowId',
  });

  const {
    fields: miscCostFields,
    append: appendMiscCost,
    remove: removeMiscCost,
  } = useFieldArray({
    control,
    name: 'miscCosts',
    keyName: 'rowId',
  });

  const finalQuantityPcs = watch('finalQuantityPcs');
  const finalProductMode = watch('finalProductMode');
  const finalProductItemId = watch('finalProductItemId');
  const finalProductName = watch('finalProductName');

  const components = useWatch({
    control,
    name: 'components',
  }) || EMPTY_COMPONENTS;

  const miscCosts = useWatch({
    control,
    name: 'miscCosts',
  }) || EMPTY_MISC_COSTS;

  const packSizeKg = watch('packSizeKg');

  const totalInputKg = useMemo(() => {
    return components.reduce((sum, c) => {
      const qty = c && typeof c.quantityKg === 'number' ? c.quantityKg : 0;
      return sum + qty;
    }, 0);
  }, [components]);

  const totalMiscCost = useMemo(() => {
    return miscCosts.reduce((sum, c) => {
      const unitPrice = typeof c?.unitPrice === 'number' ? c.unitPrice : 0;
      const unitCount = typeof c?.unit === 'number' ? c.unit : 0;
      return sum + unitPrice * unitCount;
    }, 0);
  }, [miscCosts]);

  // --- COGS: the BACKEND is the single source of truth ------------------------
  // The client no longer recomputes roasted-bulk cost (that independent, weighted
  // -average calculation is exactly what drifted from the booked FIFO value).
  // Instead:
  //   * A COMPLETED production shows its real booked cost (draftDetail.*).
  //   * A draft / new production shows a live FIFO estimate from the backend,
  //     priced by the same code that books the production, so preview == stored.
  const bookedCost = useMemo(() => {
    const tc = draftDetail?.total_cogs;
    const uc = draftDetail?.unit_cost_per_piece;
    if (typeof tc === 'number' && typeof uc === 'number') {
      return { totalCogs: tc, unitCostPerPiece: uc };
    }
    return null;
  }, [draftDetail]);

  const estimatePayload = useMemo<FinishedGoodsCostEstimateRequest | null>(() => {
    const validComponents = (components || [])
      .filter(c => c?.roastedBulkItemId && c?.roastType && typeof c.quantityKg === 'number' && c.quantityKg > 0)
      .map(c => ({
        item_id: c.roastedBulkItemId as string,
        roast_type: c.roastType as string,
        quantity_kg: c.quantityKg as number,
      }));
    if (validComponents.length === 0) return null;
    const validMisc = (miscCosts || [])
      .filter(m => m?.name && m.name.trim() && typeof m.unitPrice === 'number' && m.unitPrice > 0 && typeof m.unit === 'number' && m.unit > 0)
      .map(m => ({ name: m.name as string, unit_price: m.unitPrice as number, unit: m.unit as number }));
    return {
      final_quantity_pcs: finalQuantityPcs || 0,
      components: validComponents,
      misc_costs: validMisc,
    };
  }, [components, miscCosts, finalQuantityPcs]);

  // Only the payload contents matter; stringify so identical inputs don't refetch.
  const estimateSignature = estimatePayload ? JSON.stringify(estimatePayload) : '';

  const [costEstimate, setCostEstimate] = useState<FinishedGoodsCostEstimateResponse | null>(null);
  const estimateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const estimateReqRef = useRef(0);

  useEffect(() => {
    if (!isOpen) return;
    // Completed productions use their booked cost; no estimate needed.
    if (bookedCost) return;
    if (!estimatePayload) {
      setCostEstimate(null);
      return;
    }
    if (estimateTimerRef.current) clearTimeout(estimateTimerRef.current);
    const reqId = ++estimateReqRef.current;
    estimateTimerRef.current = setTimeout(async () => {
      try {
        const resp = await inventoryService.estimateFinishedGoodsProductionCost(estimatePayload);
        // Ignore out-of-order responses so the latest edit always wins.
        if (reqId === estimateReqRef.current) setCostEstimate(resp);
      } catch {
        // Keep the last estimate on a transient failure rather than flashing 0.
      }
    }, 400);
    return () => {
      if (estimateTimerRef.current) clearTimeout(estimateTimerRef.current);
    };
    // estimateSignature encodes the meaningful payload; estimatePayload is derived from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, bookedCost, estimateSignature]);

  const totalCogs = bookedCost?.totalCogs ?? costEstimate?.total_cogs ?? 0;
  const unitCostPerPiece = bookedCost?.unitCostPerPiece ?? costEstimate?.unit_cost_per_piece ?? 0;

  useEffect(() => {
    if (!isOpen) return;
    if (packSizeKg > 0) return;
    if (!finalQuantityPcs || finalQuantityPcs <= 0) return;

    const qty = finalQuantityPcs || 0;
    let hasChanges = false;
    const updated = (components || []).map(c => {
      const perPieceGram = typeof c.basePerPieceKg === 'number' ? c.basePerPieceKg * 1000 : 0;
      const gramsTotal = qty > 0 ? perPieceGram * qty : 0;
      const nextGrams = parseFloat(gramsTotal.toFixed(3));
      const nextQtyKg = parseFloat((gramsTotal / 1000).toFixed(3));
      if (
        Math.abs((c.gramsPerPack || 0) - nextGrams) > 0.001 ||
        Math.abs((c.quantityKg || 0) - nextQtyKg) > 0.001
      ) {
        hasChanges = true;
      }
      return { ...c, gramsPerPack: nextGrams, quantityKg: nextQtyKg };
    });
    if (hasChanges) {
      setValue('components', updated, { shouldValidate: false, shouldDirty: true });
    }
  }, [isOpen, mode, packSizeKg, finalQuantityPcs, components, setValue]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setLoading(true);
    setError(null);

    const loadData = async () => {
      try {
        const [finishedGoods, roastedGroups] = await Promise.all([
          inventoryService.getFinishedGoodsItems({ includeOnboardingSamples: onboardingStep5 }),
          inventoryService.getRoastedBulkGroups(true, { excludeOrphans: true, includeOnboardingSamples: onboardingStep5 }),
        ]);

        const pcsItems = finishedGoods.filter(item => item.unitOfMeasure === 'pcs' && item.itemType === 'finished_good');
        setFinishedGoodsItems(onboardingStep5 ? pcsItems.filter(i => i.isOnboardingSample) : pcsItems);
        // A roasted-bulk GROUP is keyed by its source GREEN BEAN, not by the
        // roasted-bulk item: GetRoastedBulkGroups emits `base.id AS item_id`
        // where base = source_green_bean_item_id (import branch) / w.item_id (WO
        // branch). Matching against roastedBulkId1/2 (roasted-bulk item ids)
        // therefore never hit, and the practice packing step showed an empty
        // picker. Match the sample green bean instead — that keeps both seeded
        // sample roasts AND the bulk the user just roasted in step 3.
        // Fail open if the ref is somehow missing: an empty picker is a hard
        // block, a slightly wider one is not.
        const sampleGreenBeanId = sampleRefs?.greenItemId;
        const visibleGroups = onboardingStep5 && sampleGreenBeanId
          ? roastedGroups.filter(g => g.itemId === sampleGreenBeanId)
          : roastedGroups;
        setRoastedBulkGroups(visibleGroups);
        // During onboarding step 5 the modal must always start in "Create New"
        // mode (build the practice finished good from scratch), even when a
        // leftover practice FG already exists — otherwise this async load would
        // flip it back to "existing" and skip the guided step-by-step.
        const nextFinalProductMode =
          onboardingStep5 && mode === 'create_draft'
            ? 'new'
            : mode === 'create_draft' && pcsItems.length === 0
              ? 'new'
              : 'existing';
        setValue('finalProductMode', nextFinalProductMode, {
          shouldValidate: true,
        });
      } catch {
        setError(t('finishedGoodsProduction.errors.loadDataFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, mode, onboardingStep5, setValue, t, sampleRefs]);

  useEffect(() => {
    if (!isOpen) {
      reset({
        finalProductMode: 'existing',
        finalProductItemId: undefined,
        finalProductName: '',
        finalProductLocation: '',
        estimatedSellingPrice: undefined,
        finalQuantityPcs: 0,
        packSizeKg: 0,
        components: [defaultComponentRow()],
        miscCosts: [defaultMiscCostRow()],
      });
      setFinalQuantityInput('');
      setComponentQuantityInputs({});
      setDraftDetail(null);
      setHasLoadedDraftData(false);
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (mode === 'create_draft') {
      setDraftDetail(null);
      setHasLoadedDraftData(false);
      return;
    }
    if (!productionId) {
      setError(t('finishedGoodsProduction.errors.missingProductionId'));
      return;
    }
    if (hasLoadedDraftData) {
      return;
    }

    let aborted = false;
    const loadDraft = async () => {
      setLoading(true);
      setError(null);
      try {
        const detail = await inventoryService.getFinishedGoodsProduction(productionId);
        if (aborted) return;
        setDraftDetail(detail);

        const qty = typeof detail.qty_produced === 'number' ? detail.qty_produced : 0;
        setValue('finalProductMode', 'existing', { shouldValidate: true });
        setValue('finalProductItemId', detail.fg_id, { shouldValidate: true });
        setValue('finalQuantityPcs', qty, { shouldValidate: true });
        setFinalQuantityInput(qty > 0 ? String(qty) : '');

        const packSizeGrams =
          typeof detail.finished_good?.pack_size === 'number'
            ? detail.finished_good.pack_size
            : finishedGoodsItems.find(i => i.id === detail.fg_id)?.packSizeGrams;
        if (packSizeGrams && packSizeGrams > 0) {
          setValue('packSizeKg', packSizeGrams / 1000, { shouldValidate: true });
        }

        const location =
          typeof detail.finished_good?.location === 'string'
            ? detail.finished_good.location
            : finishedGoodsItems.find(i => i.id === detail.fg_id)?.location;
        if (typeof location === 'string') {
          setValue('finalProductLocation', location, { shouldDirty: true });
        }

        const estimatedSellingPrice =
          typeof detail.finished_good?.estimated_selling_price === 'number'
            ? detail.finished_good.estimated_selling_price
            : finishedGoodsItems.find(i => i.id === detail.fg_id)?.estimatedSellingPrice;
        if (typeof estimatedSellingPrice === 'number') {
          setValue('estimatedSellingPrice', estimatedSellingPrice, { shouldDirty: true });
        }

        const rows = (detail.ingredients || []).map(ing => {
          const gramsPerPiece = typeof ing.gram === 'number' ? ing.gram : 0;
          const totalGramsForComponent = qty > 0 ? gramsPerPiece * qty : 0;
          const basePerPieceKg = gramsPerPiece > 0 ? gramsPerPiece / 1000 : 0;
          const quantityKg = totalGramsForComponent > 0 ? totalGramsForComponent / 1000 : 0;
          const group = roastedBulkGroups.find(g => g.itemId === ing.bean_id && g.roastType === ing.roast_type);
          const availableKg = (group?.totalStock ?? 0) + quantityKg;
          return {
            id: Math.random().toString(36).slice(2),
            roastedBulkItemId: ing.bean_id,
            roastType: ing.roast_type,
            itemName: group?.itemName,
            availableKg,
            quantityKg: parseFloat(quantityKg.toFixed(3)),
            basePerPieceKg,
            gramsPerPack: parseFloat(totalGramsForComponent.toFixed(3)),
            finalGramsPerPack: parseFloat(totalGramsForComponent.toFixed(3)),
            percentage: typeof ing.percentage === 'number' ? ing.percentage : 0,
          };
        });

        if (rows.length > 0) {
          setValue('components', rows, { shouldValidate: true, shouldDirty: true });
          setComponentQuantityInputs(() => {
            const next: Record<string, { percentage?: string; grams?: string }> = {};
            rows.forEach(r => {
              next[r.id] = {
                percentage: typeof r.percentage === 'number' && r.percentage > 0 ? String(r.percentage) : '',
                grams: typeof r.gramsPerPack === 'number' && r.gramsPerPack > 0 ? String(r.gramsPerPack) : '',
              };
            });
            return next;
          });
        }

        const miscRows = Array.isArray(detail.misc_costs)
          ? detail.misc_costs.map(m => ({
              id: Math.random().toString(36).slice(2),
              name: m.name,
              unitPrice: typeof m.unit_price === 'number' ? m.unit_price : 0,
              unit: typeof m.unit === 'number' ? m.unit : 0,
            }))
          : [];
        setValue('miscCosts', miscRows.length > 0 ? miscRows : [defaultMiscCostRow()], {
          shouldDirty: true,
          shouldValidate: true,
        });

        setHasLoadedDraftData(true);
      } catch (err: unknown) {
        const errorResponse = err as { response?: { data?: { error?: string; message?: string } } };
        const message =
          errorResponse?.response?.data?.error ||
          errorResponse?.response?.data?.message ||
          t('finishedGoodsProduction.errors.loadDraftFailed');
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    loadDraft();
    return () => {
      aborted = true;
    };
  }, [isOpen, mode, productionId, hasLoadedDraftData, roastedBulkGroups, finishedGoodsItems, setValue, t]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (mode === 'create_draft') {
      return;
    }
    if (!hasLoadedDraftData) {
      return;
    }
    if (roastedBulkGroups.length === 0) {
      return;
    }

    const current = getValues('components');
    if (!Array.isArray(current) || current.length === 0) {
      return;
    }

    let changed = false;
    const next = current.map(c => {
      const itemId = c?.roastedBulkItemId;
      const roastType = c?.roastType;
      if (!itemId || !roastType) {
        return c;
      }
      const group = roastedBulkGroups.find(g => g.itemId === itemId && g.roastType === roastType);
      if (!group) {
        return c;
      }
      const quantityKg = typeof c.quantityKg === 'number' ? c.quantityKg : 0;
      const nextAvailableKg = (group.totalStock ?? 0) + quantityKg;
      const currentAvailableKg = typeof c.availableKg === 'number' ? c.availableKg : 0;
      if (Math.abs(currentAvailableKg - nextAvailableKg) <= 0.000001) {
        return c;
      }
      changed = true;
      return { ...c, availableKg: nextAvailableKg };
    });

    if (changed) {
      setValue('components', next, { shouldDirty: false, shouldValidate: false });
    }
  }, [isOpen, mode, hasLoadedDraftData, roastedBulkGroups, getValues, setValue]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (finalProductMode !== 'existing') {
      return;
    }
    if (!finalProductItemId) {
      return;
    }
    const selected = finishedGoodsItems.find(item => item.id === finalProductItemId);
    if (!selected) {
      return;
    }
    setValue('finalProductLocation', selected.location || '', { shouldDirty: true });
    setValue('estimatedSellingPrice', selected.estimatedSellingPrice, { shouldDirty: true });

    if (selected.packSizeGrams && Number(selected.packSizeGrams) > 0) {
      const packKg = Number(selected.packSizeGrams) / 1000;
      setValue('packSizeKg', packKg, { shouldDirty: true, shouldValidate: true });
    }
    // Populate saved ingredient configuration (if available)
    if (hasLoadedDraftData && (draftDetail?.ingredients || []).length > 0) {
      return;
    }
    let aborted = false;
    const loadSavedIngredients = async () => {
      try {
        if (!selected?.id) return;
        const packKg = getValues('packSizeKg');
        const [list, miscList] = await Promise.all([
          inventoryService.getFinishedGoodIngredients(selected.id),
          inventoryService.getFinishedGoodMiscCosts(selected.id),
        ]);
        if (aborted) return;
        const currentFinalQty = getValues('finalQuantityPcs') || 0;
        const packGrams = packKg && packKg > 0 ? packKg * 1000 : 0;
        const rows = list.map(ing => {
          const gramsPerPiece = Number.isFinite(Number(ing.grams_per_pack)) ? Number(ing.grams_per_pack) : 0;
          const basePerPieceKg = gramsPerPiece > 0 ? gramsPerPiece / 1000 : 0;
          const totalGrams = gramsPerPiece > 0 && currentFinalQty > 0 ? gramsPerPiece * currentFinalQty : gramsPerPiece;
          const quantityKg = basePerPieceKg > 0 && currentFinalQty > 0 ? basePerPieceKg * currentFinalQty : basePerPieceKg;
          const ingPct = Number.isFinite(Number(ing.percentage)) ? Number(ing.percentage) : undefined;
          const p = typeof ingPct === 'number' && ingPct >= 0 ? ingPct : (packGrams > 0 ? (gramsPerPiece / packGrams) * 100 : 0);
          const group = roastedBulkGroups.find(g => g.itemId === ing.inventory_item_id && g.roastType === ing.roast_type);
          const availableKg = group?.totalStock ?? 0;
          const itemName = ing.item_name || group?.itemName;
          return {
            id: Math.random().toString(36).slice(2),
            roastedBulkItemId: ing.inventory_item_id,
            roastType: ing.roast_type,
            itemName,
            availableKg,
            quantityKg: parseFloat((quantityKg).toFixed(3)),
            basePerPieceKg,
            gramsPerPack: parseFloat((totalGrams).toFixed(3)),
            percentage: parseFloat((p).toFixed(2)),
          };
        });

        if (rows.length > 0) {
          setValue('components', rows, { shouldValidate: true, shouldDirty: true });
          setComponentQuantityInputs(() => {
            const next: Record<string, { percentage?: string; grams?: string }> = {};
            rows.forEach(r => {
              next[r.id] = {
                percentage: r.percentage ? r.percentage.toString() : '',
                grams: r.gramsPerPack ? r.gramsPerPack.toString() : '',
              };
            });
            return next;
          });
        }
        if (Array.isArray(miscList)) {
          const miscRows = miscList.map(m => ({
            id: Math.random().toString(36).slice(2),
            name: m.name,
            unitPrice: Number.isFinite(Number(m.unit_price)) ? Number(m.unit_price) : 0,
            unit: Number.isFinite(Number(m.unit)) ? Number(m.unit) : 0,
          }));
          setValue('miscCosts', miscRows.length > 0 ? miscRows : [defaultMiscCostRow()], { shouldDirty: true, shouldValidate: true });
        }
      } catch {
        // Silent fail: keep defaults
      }
    };
    // Defer execution to allow pack size state to settle
    setTimeout(loadSavedIngredients, 0);
    return () => {
      aborted = true;
    };
  }, [isOpen, mode, hasLoadedDraftData, draftDetail, finalProductMode, finalProductItemId, finishedGoodsItems, roastedBulkGroups, getValues, setValue]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setValue('totalInputCost', totalCogs, { shouldDirty: false, shouldValidate: false });
    setValue('unitCostPerPiece', unitCostPerPiece, { shouldDirty: false, shouldValidate: false });
  }, [isOpen, totalCogs, unitCostPerPiece, setValue]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const safePackKg = packSizeKg > 0 ? packSizeKg : 0;
    const packGrams = safePackKg > 0 ? safePackKg * 1000 : 0;
    const effectiveFinalQty = finalQuantityPcs > 0 ? finalQuantityPcs : 0;
    const currentComponents = getValues('components');

    let hasChanges = false;

    const updatedComponents = currentComponents.map(component => {
      const percentage = typeof component.percentage === 'number' ? component.percentage : 0;
      const gramsPerPiece = packGrams > 0 ? (percentage / 100) * packGrams : 0;
      const totalGramsForComponent = gramsPerPiece > 0 && effectiveFinalQty > 0 ? gramsPerPiece * effectiveFinalQty : 0;
      const basePerPieceKg = gramsPerPiece > 0 ? gramsPerPiece / 1000 : 0;
      const quantityKg = basePerPieceKg > 0 && effectiveFinalQty > 0 ? basePerPieceKg * effectiveFinalQty : 0;

      const newGramsPerPack = parseFloat(totalGramsForComponent.toFixed(3));
      const newQuantityKg = parseFloat(quantityKg.toFixed(3));

      if (
        Math.abs((component.gramsPerPack || 0) - newGramsPerPack) > 0.001 ||
        Math.abs((component.quantityKg || 0) - newQuantityKg) > 0.001 ||
        Math.abs((component.basePerPieceKg || 0) - basePerPieceKg) > 0.000001
      ) {
        hasChanges = true;
      }

      return {
        ...component,
        gramsPerPack: newGramsPerPack,
        basePerPieceKg,
        quantityKg: newQuantityKg,
      };
    });

    if (hasChanges) {
      setValue('components', updatedComponents, { shouldValidate: true, shouldDirty: true });

      setComponentQuantityInputs(prev => {
        const next: typeof prev = { ...prev };
        updatedComponents.forEach((component) => {
          if (!component.id) return;
          const fieldId = component.id;
          const existing = next[fieldId] || {};
          next[fieldId] = {
            ...existing,
            grams:
              typeof component.gramsPerPack === 'number' && component.gramsPerPack > 0
                ? component.gramsPerPack.toString()
                : '',
            percentage:
              existing.percentage ??
              (typeof component.percentage === 'number' && component.percentage > 0
                ? component.percentage.toFixed(2)
                : ''),
          };
        });
        return next;
      });
    }
  }, [isOpen, mode, packSizeKg, finalQuantityPcs, getValues, setValue]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (mode !== 'complete_draft') {
      prevFinalQuantityRef.current = 0;
      return;
    }
    if (!finalQuantityPcs || finalQuantityPcs <= 0) {
      prevFinalQuantityRef.current = 0;
      return;
    }

    const prevQty = prevFinalQuantityRef.current;
    if (!prevQty || prevQty <= 0) {
      prevFinalQuantityRef.current = finalQuantityPcs;
      return;
    }
    if (Math.abs(prevQty - finalQuantityPcs) <= 0.000001) {
      return;
    }

    const currentComponents = getValues('components');
    let hasChanges = false;

    const updatedComponents = currentComponents.map(component => {
      if (typeof component.finalGramsPerPack !== 'number' || component.finalGramsPerPack <= 0) {
        return component;
      }
      hasChanges = true;
      return { ...component, finalGramsPerPack: undefined };
    });

    prevFinalQuantityRef.current = finalQuantityPcs;

    if (hasChanges) {
      setValue('components', updatedComponents, { shouldValidate: true, shouldDirty: true });
    }
  }, [isOpen, mode, finalQuantityPcs, getValues, setValue]);

  const totalPercentage = useMemo(() => {
    return components.reduce((sum, c) => sum + (typeof c.percentage === 'number' ? c.percentage : 0), 0);
  }, [components]);

  const totalGrams = useMemo(() => {
    return components.reduce((sum, c) => sum + (typeof c.gramsPerPack === 'number' ? c.gramsPerPack : 0), 0);
  }, [components]);

  const maxQuantity = useMemo(() => {
    const eligible = components.filter(
      c => c?.roastedBulkItemId && typeof c.availableKg === 'number' && typeof c.basePerPieceKg === 'number'
    );
    if (eligible.length === 0) return 0;
    let max = Infinity;
    eligible.forEach(c => {
      const base = c.basePerPieceKg || 0;
      if (base <= 0) return;
      const possible = Math.floor((c.availableKg || 0) / base);
      if (possible < max) max = possible;
    });
    if (!Number.isFinite(max)) return 0;
    return Math.max(0, max);
  }, [components]);

  const handleCalculateMaxQuantity = () => {
    const max = maxQuantity;
    setFinalQuantityInput(max > 0 ? String(max) : '0');
    setValue('finalQuantityPcs', max, { shouldValidate: true });
  };

  const roastedBulkOptions = useMemo(() => {
    return roastedBulkGroups
      .filter(group => (group.totalStock || 0) > 0)
      .map(group => {
        const remaining = group.totalStock || 0;
        return {
          id: `${group.itemId}:${group.roastType}`,
          value: `${group.itemId}:${group.roastType}`,
          label: `${group.itemName} – ${group.roastType} (remaining: ${remaining.toFixed(3)} kg)`,
          text: group.itemName,
          roastType: group.roastType,
          itemId: group.itemId,
          availableKg: remaining,
        };
      });
  }, [roastedBulkGroups]);

  const getRoastedBulkLabel = (itemId?: string, roastType?: string): string | undefined => {
    if (!itemId || !roastType) return undefined;
    const group = roastedBulkGroups.find(g => g.itemId === itemId && g.roastType === roastType);
    return group?.itemName;
  };

  const finishedGoodsOptions = useMemo(() => {
    return finishedGoodsItems.map(item => ({
      ...item,
      id: item.id,
      value: item.id,
      label: `${item.name} (${item.code})`,
      text: item.name,
    }));
  }, [finishedGoodsItems]);

  const existingFinishedGoodNameSet = useMemo(() => {
    const set = new Set<string>();
    finishedGoodsItems.forEach(item => {
      const normalized = normalizeFinishedGoodName(item.name);
      if (normalized) set.add(normalized);
    });
    return set;
  }, [finishedGoodsItems]);

  // Onboarding step 5 must open the modal in "Create New" mode so the user
  // builds the practice finished good from scratch. The async load also enforces
  // this, but setting it here avoids a flash of "Use Existing" before that
  // resolves. No field values are pre-filled — the guide walks the user through
  // entering each one themselves (name, pack size, quantity, roast).
  useEffect(() => {
    if (!onboardingStep5 || mode !== 'create_draft') return;
    if (getValues('finalProductMode') !== 'new') setValue('finalProductMode', 'new');
  }, [onboardingStep5, mode, getValues, setValue]);

  // Note: a single picked component is allocated to 100% by handleComponentSelectChange
  // and its total grams are derived by the pack-size/quantity recompute effect above —
  // exactly as in the normal (non-onboarding) flow. No onboarding-specific allocation
  // is needed here; an earlier helper that forced gramsPerPack to the per-pack value
  // conflicted with that effect and disabled Save once a quantity was entered.

  const isDuplicateFinalProductName = useMemo(() => {
    // Onboarding always creates a fresh practice finished good; a leftover
    // practice FG with the same name must not disable submit (the backend
    // ignores onboarding-sample rows in its uniqueness check).
    if (onboardingStep5) return false;
    if (finalProductMode !== 'new') return false;
    const normalized = normalizeFinishedGoodName(finalProductName);
    if (!normalized) return false;
    return existingFinishedGoodNameSet.has(normalized);
  }, [onboardingStep5, finalProductMode, finalProductName, existingFinishedGoodNameSet]);

  const handleModeSwitch = (mode: FinalProductMode) => {
    reset(
      {
        finalProductMode: mode,
        finalProductItemId: undefined,
        finalProductName: '',
        finalProductLocation: '',
        estimatedSellingPrice: undefined,
        finalQuantityPcs: 0,
        packSizeKg: 0,
        components: [defaultComponentRow()],
        miscCosts: [defaultMiscCostRow()],
      },
      { keepDirty: false, keepErrors: false, keepIsSubmitted: false, keepTouched: false },
    );
    setFinalQuantityInput('');
    setComponentQuantityInputs({});
    setError(null);
  };

  const handleComponentSelectChange = (index: number, value: string) => {
    const currentComponent = getValues(`components.${index}`);
    const option = roastedBulkOptions.find(o => o.value === value);
    if (!option) {
      update(index, {
        ...fields[index],
        roastedBulkItemId: '',
        roastType: '',
        itemName: undefined,
        availableKg: 0,
        quantityKg: 0,
        basePerPieceKg: 0,
        gramsPerPack: 0,
        percentage: 0,
      });
      setComponentQuantityInputs(prev => {
        const next = { ...prev };
        if (currentComponent?.id) {
          delete next[currentComponent.id];
        }
        return next;
      });
      return;
    }

    // Update component in a temporary array to calculate distribution
    const currentComponents = getValues('components');
    const updatedComponents = [...currentComponents];
    
    // Ensure the updated row has the new item details
    updatedComponents[index] = {
      ...updatedComponents[index],
      roastedBulkItemId: option.itemId,
      roastType: option.roastType,
      itemName: option.text,
      availableKg: option.availableKg,
      // Reset values initially
      quantityKg: 0,
      basePerPieceKg: 0,
      gramsPerPack: 0,
      percentage: 0,
    };

    const selectedIndexes = updatedComponents
      .map((c, i) => (c?.roastedBulkItemId && c?.roastType ? i : -1))
      .filter(i => i >= 0);

    const currentFinalQty = getValues('finalQuantityPcs');
    const safePackKg = packSizeKg > 0 ? packSizeKg : 0;
    const packGrams = safePackKg > 0 ? safePackKg * 1000 : 0;

    const n = selectedIndexes.length;
    if (n > 0) {
      const base = Math.floor(100 / n);
      const remainder = 100 - base * n;
      
      selectedIndexes.forEach((compIndex, idx) => {
        const p = base + (idx < remainder ? 1 : 0);
        const gramsPerPack = packGrams > 0 ? (p / 100) * packGrams : 0;
        const basePerPieceKg = gramsPerPack > 0 ? gramsPerPack / 1000 : 0;
        const quantityKg = currentFinalQty > 0 ? basePerPieceKg * currentFinalQty : 0;

        updatedComponents[compIndex] = {
          ...updatedComponents[compIndex],
          percentage: p,
          gramsPerPack: parseFloat(((currentFinalQty > 0 ? gramsPerPack * currentFinalQty : gramsPerPack)).toFixed(3)),
          basePerPieceKg,
          quantityKg: parseFloat(quantityKg.toFixed(3)),
        };

        // Update inputs
        const fid = updatedComponents[compIndex].id;
        if (fid) {
           setComponentQuantityInputs(prev => ({
             ...prev,
             [fid]: { ...prev[fid], percentage: String(p), grams: undefined }
           }));
        }
      });
      
      setValue('components', updatedComponents, { shouldValidate: true, shouldDirty: true });
    } else {
       // Should not be reached if we just selected an option, but fallback
       update(index, {
        ...fields[index],
        roastedBulkItemId: option.itemId,
        roastType: option.roastType,
        itemName: option.text,
        availableKg: option.availableKg,
        quantityKg: 0,
        basePerPieceKg: 0,
        gramsPerPack: 0,
        percentage: 0,
      });
    }
  };

  const availableOptionsForRow = (index: number) => {
    const selectedKeys = components
      .map((c, i) =>
        i === index || !c?.roastedBulkItemId || !c?.roastType
          ? null
          : `${c.roastedBulkItemId}:${c.roastType}`
      )
      .filter((v): v is string => Boolean(v));

    return roastedBulkOptions.filter(option => !selectedKeys.includes(option.value));
  };

  const handlePercentageChange = (index: number, raw: string) => {
    if (raw !== '' && !/^\d{0,3}$/.test(raw)) {
      return;
    }
    const parsed = raw === '' ? 0 : parseInt(raw, 10);
    
    // Find all valid indexes
    const validIndexes = getValues('components')
      .map((c, i) => (c?.roastedBulkItemId && c?.roastType ? i : -1))
      .filter(i => i >= 0);

    const safePackKg = packSizeKg > 0 ? packSizeKg : 0;
    const packGrams = safePackKg > 0 ? safePackKg * 1000 : 0;
    const currentFinalQty = getValues('finalQuantityPcs');

    const updateRow = (idx: number, p: number) => {
      const gramsPerPack = packGrams > 0 ? (p / 100) * packGrams : 0;
      const basePerPieceKg = gramsPerPack > 0 ? gramsPerPack / 1000 : 0;
      const quantityKg = currentFinalQty > 0 ? basePerPieceKg * currentFinalQty : 0;
      
      setValue(`components.${idx}.percentage`, p, { shouldValidate: true });
      setValue(
        `components.${idx}.gramsPerPack`,
        parseFloat(((currentFinalQty > 0 ? gramsPerPack * currentFinalQty : gramsPerPack)).toFixed(3)),
        { shouldValidate: true }
      );
      setValue(`components.${idx}.basePerPieceKg`, basePerPieceKg, { shouldValidate: true });
      setValue(`components.${idx}.quantityKg`, parseFloat(quantityKg.toFixed(3)), { shouldValidate: true });

      const component = getValues(`components.${idx}`);
      const fieldId = component?.id;
      if (fieldId) {
        setComponentQuantityInputs(prev => {
          const existing = prev[fieldId] || {};
          return {
            ...prev,
            [fieldId]: {
              ...existing,
              percentage: String(p),
              grams: undefined,
            },
          };
        });
      }
    };

    const baseValue = Math.max(0, Math.min(100, parsed));
    const nextValue = baseValue;

    updateRow(index, nextValue);

    if (validIndexes.length >= 2 && validIndexes.includes(index)) {
      // For any number of components: auto-adjust the last component to maintain 100%
      const otherIndexes = validIndexes.filter(i => i !== index);
      const lastOtherIndex = otherIndexes[otherIndexes.length - 1];
      if (lastOtherIndex !== undefined) {
        const sumExceptLast = validIndexes
          .filter(i => i !== index && i !== lastOtherIndex)
          .reduce((sum, i) => sum + (Number(getValues(`components.${i}.percentage`)) || 0), 0);
        const remainder = Math.max(0, 100 - nextValue - sumExceptLast);
        updateRow(lastOtherIndex, remainder);
      }
    }
  };

  const handleComponentWeightChange = (index: number, valKg: number | undefined) => {
    if (valKg === undefined) {
      const component = getValues(`components.${index}`);
      const fieldId = component?.id;
      if (fieldId) {
        setComponentQuantityInputs(prev => {
          const existing = prev[fieldId] || {};
          return {
            ...prev,
            [fieldId]: {
              ...existing,
              grams: undefined,
              percentage: undefined,
            },
          };
        });
      }
      setValue(`components.${index}.gramsPerPack`, 0, { shouldValidate: true });
      setValue(`components.${index}.percentage`, 0, { shouldValidate: true });
      setValue(`components.${index}.basePerPieceKg`, 0, { shouldValidate: true });
      setValue(`components.${index}.quantityKg`, 0, { shouldValidate: true });
      return;
    }

    const valueGrams = valKg * 1000;

    const safePackKg = packSizeKg > 0 ? packSizeKg : 0;
    const packGrams = safePackKg > 0 ? safePackKg * 1000 : 0;
    const currentFinalQty = getValues('finalQuantityPcs');

    const effectiveFinalQty = currentFinalQty > 0 ? currentFinalQty : 1;
    const maxTotalGrams = packGrams * effectiveFinalQty;

    const component = getValues(`components.${index}`);
    const fieldId = component?.id;

    if (fieldId) {
      setComponentQuantityInputs(prev => {
        const existing = prev[fieldId] || {};
        return {
          ...prev,
          [fieldId]: {
            ...existing,
            grams: undefined,
            percentage: undefined,
          },
        };
      });
    }

    // Find all valid indexes
    const validIndexes = getValues('components')
      .map((c, i) => (c?.roastedBulkItemId && c?.roastType ? i : -1))
      .filter(i => i >= 0);

    const otherTotalGrams = validIndexes
      .filter(i => i !== index)
      .reduce((sum, i) => sum + (Number(getValues(`components.${i}.gramsPerPack`)) || 0), 0);
    const remainingGrams = Math.max(0, maxTotalGrams - otherTotalGrams);
    let finalValueGrams = Math.min(valueGrams, remainingGrams);

    const gramsPerPiece = effectiveFinalQty > 0 ? finalValueGrams / effectiveFinalQty : 0;
    const percentageFloat = packGrams > 0 ? (gramsPerPiece / packGrams) * 100 : 0;
    let percentageInt = Math.max(0, Math.min(100, Math.round(percentageFloat)));
    if (validIndexes.length > 2 && validIndexes.includes(index)) {
      const sumOthers = validIndexes
        .filter(i => i !== index)
        .reduce((sum, i) => sum + (Number(getValues(`components.${i}.percentage`)) || 0), 0);
      const maxAllowed = Math.max(0, 100 - sumOthers);
      percentageInt = Math.min(percentageInt, maxAllowed);
      finalValueGrams =
        packGrams > 0 ? parseFloat(((percentageInt / 100) * packGrams * effectiveFinalQty).toFixed(3)) : 0;
    }
      
    const updateRow = (idx: number, p: number, grams: number) => {
         if (idx === index) {
             // Target row - use explicit grams
             const totalKg = grams > 0 ? grams / 1000 : 0;
             const basePerPieceKg = effectiveFinalQty > 0 ? totalKg / effectiveFinalQty : 0;
             const quantityKg = currentFinalQty > 0 ? basePerPieceKg * currentFinalQty : 0;
             setValue(`components.${idx}.gramsPerPack`, grams, { shouldValidate: true });
             setValue(`components.${idx}.percentage`, p, { shouldValidate: true });
             setValue(`components.${idx}.basePerPieceKg`, basePerPieceKg, { shouldValidate: true });
             setValue(`components.${idx}.quantityKg`, parseFloat(quantityKg.toFixed(3)), { shouldValidate: true });
             
              if (fieldId) {
                setComponentQuantityInputs(prev => {
                  const existing = prev[fieldId] || {};
                  return {
                    ...prev,
                    [fieldId]: {
                      ...existing,
                      percentage: String(p),
                      grams: undefined,
                    },
                  };
                });
              }
         } else {
             // Other row - derive from percentage
             const otherGramsPerPack = packGrams > 0 ? (p / 100) * packGrams : 0;
             const otherBasePerPieceKg = otherGramsPerPack > 0 ? otherGramsPerPack / 1000 : 0;
             const quantityKg = currentFinalQty > 0 ? otherBasePerPieceKg * currentFinalQty : 0;
             
             setValue(
               `components.${idx}.gramsPerPack`,
               parseFloat(((currentFinalQty > 0 ? otherGramsPerPack * currentFinalQty : otherGramsPerPack)).toFixed(3)),
               { shouldValidate: true }
             );
             setValue(`components.${idx}.percentage`, p, { shouldValidate: true });
             setValue(`components.${idx}.basePerPieceKg`, otherBasePerPieceKg, { shouldValidate: true });
             setValue(`components.${idx}.quantityKg`, parseFloat(quantityKg.toFixed(3)), { shouldValidate: true });
             
             const otherComp = getValues(`components.${idx}`);
             const otherFid = otherComp?.id;
             if (otherFid) {
                setComponentQuantityInputs(prev => {
                  const existing = prev[otherFid] || {};
                  return {
                    ...prev,
                    [otherFid]: {
                      ...existing,
                      percentage: String(p),
                      grams: undefined,
                    },
                  };
                });
             }
         }
    };
    
    updateRow(index, percentageInt, finalValueGrams);
    
    if (validIndexes.length === 2 && validIndexes.includes(index)) {
        const otherIndex = validIndexes.find(i => i !== index);
        if (otherIndex !== undefined) {
            const otherValue = Math.max(0, 100 - percentageInt);
            // We pass 0 for grams because updateRow handles 'other' logic by calculating from percentage
            updateRow(otherIndex, otherValue, 0); 
        }
    }
  };

  const handleAddComponent = () => {
    append(defaultComponentRow());
  };

  const handleAddMiscCost = () => {
    appendMiscCost(defaultMiscCostRow());
  };

  const handleFinalComponentWeightChange = (index: number, valKg: number | undefined) => {
    const currentFinalQty = getValues('finalQuantityPcs');
    const gramsTotal = typeof valKg === 'number' && valKg >= 0 ? Math.round(valKg * 1000) : 0;
    const basePerPieceKg = currentFinalQty > 0 ? (gramsTotal / currentFinalQty) / 1000 : 0;
    const quantityKg = gramsTotal > 0 ? gramsTotal / 1000 : 0;
    setValue(`components.${index}.finalGramsPerPack`, gramsTotal, { shouldValidate: true });
    setValue(`components.${index}.basePerPieceKg`, basePerPieceKg, { shouldValidate: true });
    setValue(`components.${index}.quantityKg`, parseFloat(quantityKg.toFixed(3)), { shouldValidate: true });
  };

  const hasConflictingDraft = async (finishedGoodId?: string) => {
    if (!finishedGoodId) return false;

    try {
      const drafts = await inventoryService.listFinishedGoodsProductions({
        status: 'draft',
        finishedGoodId,
      });

      const existingDraft = drafts.find(
        (draft: FinishedGoodsProductionListItem) =>
          draft.status === 'draft' && draft.fg_id === finishedGoodId
      );

      if (!existingDraft) {
        return false;
      }

      const fgName =
        existingDraft.fg_name ||
        finishedGoodsItems.find(item => item.id === finishedGoodId)?.name ||
        'this finished good';

      const draftCode = existingDraft.code || existingDraft.id;

      const message = `${fgName} already has an active draft (${draftCode}). Please complete or cancel the existing draft before creating a new one.`;

      setError(message);
      showToast(message, 'error');

      return true;
    } catch {
      const message =
        'Unable to check for existing draft records. Please try again.';

      setError(message);
      showToast(message, 'error');

      return true;
    }
  };

  const handleSubmitForm = async (values: FinishedGoodsProductionFormValues) => {
    setSubmitting(true);
    setError(null);

    try {
      if (mode === 'view') {
        const ingredientsPayload = values.components
          .filter(c => c?.roastedBulkItemId && c?.roastType)
          .map(c => {
            const totalGrams = typeof c.gramsPerPack === 'number' ? c.gramsPerPack : 0;
            const gramsPerPiece = values.finalQuantityPcs > 0 ? totalGrams / values.finalQuantityPcs : 0;
            return {
              bean_id: c.roastedBulkItemId || '',
              roast_type: c.roastType || '',
              percentage: typeof c.percentage === 'number' ? c.percentage : 0,
              gram: Math.round(gramsPerPiece),
            };
          });

        const miscCostsPayload = values.miscCosts
          .filter(c => c && c.name && c.name.trim() && c.unitPrice > 0 && c.unit > 0)
          .map(c => ({
            name: c.name.trim(),
            unit_price: c.unitPrice,
            unit: c.unit,
          }));

        const payload: CreateFinishedGoodsProductionDraftRequest = {
          fg_id: values.finalProductItemId || '',
          qty_produced: values.finalQuantityPcs,
          finished_good: {
            location: values.finalProductLocation?.trim() || undefined,
            estimated_selling_price:
              typeof values.estimatedSellingPrice === 'number' ? values.estimatedSellingPrice : undefined,
            pack_size: values.packSizeKg > 0 ? Math.round(values.packSizeKg * 1000) : undefined,
          },
          ingredients: ingredientsPayload,
          misc_costs: miscCostsPayload,
        };

        const response = await inventoryService.createFinishedGoodsProductionDraft(payload);
        onSuccess({
          kind: 'draft_created',
          productionId: response.production_id,
          finishedGoodId: response.fg_id || values.finalProductItemId,
          finalQuantityPcs: values.finalQuantityPcs,
        });
        onClose();
        return;
      }

      if (mode === 'create_draft') {
        if (values.finalProductMode === 'existing') {
          if (await hasConflictingDraft(values.finalProductItemId)) {
            return;
          }

          const ingredientsPayload = values.components
            .filter(c => c?.roastedBulkItemId && c?.roastType)
            .map(c => {
              const totalGrams = typeof c.gramsPerPack === 'number' ? c.gramsPerPack : 0;
              const gramsPerPiece = values.finalQuantityPcs > 0 ? totalGrams / values.finalQuantityPcs : 0;
              return {
                bean_id: c.roastedBulkItemId || '',
                roast_type: c.roastType || '',
                percentage: typeof c.percentage === 'number' ? c.percentage : 0,
                gram: Math.round(gramsPerPiece),
              };
            });
          const miscCostsPayload = values.miscCosts
            .filter(c => c && c.name && c.name.trim() && c.unitPrice > 0 && c.unit > 0)
            .map(c => ({
              name: c.name.trim(),
              unit_price: c.unitPrice,
              unit: c.unit,
            }));
          const payload: CreateFinishedGoodsProductionDraftRequest = {
            fg_id: values.finalProductItemId || '',
            qty_produced: values.finalQuantityPcs,
            // The finished good row is the only storage for these attributes: the
            // complete step reads them back from the item, so omitting them here
            // silently discards whatever the user typed on the draft form.
            finished_good: {
              location: values.finalProductLocation?.trim() || undefined,
              estimated_selling_price:
                typeof values.estimatedSellingPrice === 'number' ? values.estimatedSellingPrice : undefined,
              pack_size: values.packSizeKg > 0 ? Math.round(values.packSizeKg * 1000) : undefined,
            },
            ingredients: ingredientsPayload.length > 0 ? ingredientsPayload : undefined,
            misc_costs: miscCostsPayload.length > 0 ? miscCostsPayload : undefined,
          };
          const response = await inventoryService.createFinishedGoodsProductionDraft(payload);
          onSuccess({
            kind: 'draft_created',
            productionId: response.production_id,
            finishedGoodId: response.fg_id || values.finalProductItemId,
            finalQuantityPcs: values.finalQuantityPcs,
          });
          onClose();
          return;
        }

        const trimmedName = (values.finalProductName || '').trim().replace(/\s+/g, ' ');
        if (!trimmedName) {
          setError(t('inventory.finishedGoods.finalProduct.required'));
          return;
        }
        const normalizedName = trimmedName.toLowerCase();
        const duplicate = finishedGoodsItems.some(
          item => normalizeFinishedGoodName(item.name) === normalizedName,
        );
        if (duplicate) {
          setError(t('inventory.finishedGoods.finalProduct.nameAlreadyExists'));
          return;
        }
        const payload: CreateFinishedGoodsProductionDraftNewRequest = {
          finished_good: {
            name: trimmedName,
            location: values.finalProductLocation?.trim() || undefined,
            estimated_selling_price:
              typeof values.estimatedSellingPrice === 'number' ? values.estimatedSellingPrice : undefined,
            pack_size: values.packSizeKg > 0 ? Math.round(values.packSizeKg * 1000) : undefined,
          },
          qty_produced: values.finalQuantityPcs,
          is_onboarding_sample: onboardingStep5,
          ingredients: values.components
            .filter(c => c?.roastedBulkItemId && c?.roastType)
            .map(c => {
              const totalGrams = typeof c.gramsPerPack === 'number' ? c.gramsPerPack : 0;
              const gramsPerPiece = values.finalQuantityPcs > 0 ? totalGrams / values.finalQuantityPcs : 0;
              return {
                bean_id: c.roastedBulkItemId || '',
                roast_type: c.roastType || '',
                percentage: typeof c.percentage === 'number' ? c.percentage : 0,
                gram: Math.round(gramsPerPiece),
              };
            }),
          misc_costs: values.miscCosts
            .filter(c => c && c.name && c.name.trim() && c.unitPrice > 0 && c.unit > 0)
            .map(c => ({
              name: c.name.trim(),
              unit_price: c.unitPrice,
              unit: c.unit,
            })),
        };
        const response = await inventoryService.createFinishedGoodsProductionDraftNew(payload);
        onSuccess({
          kind: 'draft_created',
          productionId: response.production_id,
          finishedGoodId: response.fg_id,
          finalQuantityPcs: values.finalQuantityPcs,
        });
        onClose();
        return;
      }

      if (!productionId) {
        setError(t('finishedGoodsProduction.errors.missingProductionId'));
        return;
      }

      const completePayload: CompleteFinishedGoodsProductionDraftRequest = {
        qty_produced: values.finalQuantityPcs,
        ingredients: values.components
          .filter(c => c?.roastedBulkItemId && c?.roastType)
          .map(c => {
            const totalGrams =
              typeof c.finalGramsPerPack === 'number' && c.finalGramsPerPack > 0
                ? c.finalGramsPerPack
                : (typeof c.gramsPerPack === 'number' ? c.gramsPerPack : 0);
            const gramsPerPiece = values.finalQuantityPcs > 0 ? totalGrams / values.finalQuantityPcs : 0;
            return {
              bean_id: c.roastedBulkItemId || '',
              roast_type: c.roastType || '',
              percentage: typeof c.percentage === 'number' ? c.percentage : 0,
              gram: Math.round(gramsPerPiece),
            };
          }),
        misc_costs: values.miscCosts
          .filter(c => c && c.name && c.name.trim() && c.unitPrice > 0 && c.unit > 0)
          .map(c => ({
            name: c.name.trim(),
            unit_price: c.unitPrice,
            unit: c.unit,
          })),
      };

      const requestPayload: FinishedGoodsProductionRequest = {
        final_product: {
          mode: 'existing',
          item_id: values.finalProductItemId,
          name: undefined,
          location: values.finalProductLocation?.trim() || undefined,
          estimated_selling_price:
            typeof values.estimatedSellingPrice === 'number' ? values.estimatedSellingPrice : undefined,
          pack_size: Math.round(values.packSizeKg * 1000),
        },
        final_quantity_pcs: values.finalQuantityPcs,
        components: values.components.map(component => ({
          item_id: component.roastedBulkItemId || '',
          roast_type: component.roastType || '',
          quantity_kg: component.quantityKg,
        })),
        misc_costs: values.miscCosts
          .filter(c => c && c.name && c.name.trim() && c.unitPrice > 0 && c.unit > 0)
          .map(c => ({
            name: c.name.trim(),
            unit_price: c.unitPrice,
            unit: c.unit,
          })),
      };

      const response = await inventoryService.completeFinishedGoodsProductionDraft(productionId, completePayload);
      onSuccess({ kind: 'completed', request: requestPayload, response, productionId });
      onClose();
    } catch (err: unknown) {
      const errorResponse = err as { response?: { data?: { error?: string; message?: string } } };
      const message =
        errorResponse?.response?.data?.error ||
        errorResponse?.response?.data?.message ||
        'Failed to produce finished goods. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const disableSubmit = useMemo(() => {
    if (submitting || loading) return true;
    if (!finalQuantityPcs || finalQuantityPcs <= 0) return true;

    if (mode === 'create_draft') {
      const hasFinalProduct =
        (finalProductMode === 'existing' && !!finalProductItemId) || (finalProductMode === 'new' && !!finalProductName);
      if (!hasFinalProduct) return true;
      if (finalProductMode === 'new' && isDuplicateFinalProductName) return true;
      if (!packSizeKg || packSizeKg <= 0) return true;

      const selectedComponents = components.filter(c => !!c?.roastedBulkItemId && !!c?.roastType);
      if (selectedComponents.length === 0) return true;
      const selectedTotalPercentage = selectedComponents.reduce((sum, c) => sum + (Number(c?.percentage) || 0), 0);
      const selectedTotalGrams = selectedComponents.reduce((sum, c) => sum + (Number(c?.gramsPerPack) || 0), 0);
      if (
        selectedComponents.some(c => {
          const pct = Number(c?.percentage) || 0;
          const grams = Number(c?.gramsPerPack) || 0;
          return pct <= 0 || grams <= 0;
        })
      ) {
        return true;
      }

      if (Math.abs(selectedTotalPercentage - 100) > 0.5) return true;
      if (Math.abs(selectedTotalGrams - packSizeKg * 1000 * finalQuantityPcs) > 0.5) return true;
      return false;
    }

    if (!productionId) return true;
    if (!packSizeKg || packSizeKg <= 0) return true;
    if (components.length === 0) return true;
    if (
      components.some(c => {
        const qty = Number(c?.quantityKg) || 0;
        const available = Number(c?.availableKg) || 0;
        return !c?.roastedBulkItemId || qty <= 0 || qty > available;
      })
    ) {
      return true;
    }
    if (Math.abs(totalPercentage - 100) > 0.5) return true;
    if (packSizeKg > 0 && finalQuantityPcs > 0 && Math.abs(totalGrams - packSizeKg * 1000 * finalQuantityPcs) > 0.5) {
      return true;
    }
    return false;
  }, [
    mode,
    submitting,
    loading,
    finalQuantityPcs,
    finalProductMode,
    finalProductItemId,
    finalProductName,
    isDuplicateFinalProductName,
    packSizeKg,
    productionId,
    components,
    totalPercentage,
    totalGrams,
  ]);

  return {
    unit,
    toggleUnit,
    control,
    handleSubmit,
    register,
    formState,
    setValue,
    watch,
    fields,
    remove,
    finalQuantityInput,
    setFinalQuantityInput,
    finishedGoodsItems,
    finalProductMode,
    isDuplicateFinalProductName,
    finalQuantityPcs,
    components,
    miscCostFields,
    packSizeKg,
    totalGrams,
    totalInputKg,
    totalInputCost: totalCogs,
    unitCostPerPiece,
    totalMiscCost,
    componentQuantityInputs,
    error,
    loading,
    submitting,
    disableSubmit,
    mode,
    productionId,
    draftDetail,
    maxQuantity,
    handleCalculateMaxQuantity,
    handleAddComponent,
    handleAddMiscCost,
    removeMiscCost,
    handleSubmitForm,
    handleComponentSelectChange,
    handlePercentageChange,
    handleComponentWeightChange,
    handleFinalComponentWeightChange,
    availableOptionsForRow,
    handleModeSwitch,
    finishedGoodsOptions,
    getRoastedBulkLabel,
  };
};
